import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';
import { ReactComponent as GetAndRideLogo } from 'icons/get-and-ride-logo-icon.svg';
import { useEffect } from 'react';
import { useRef } from 'react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'components/Context/AppContext';

const months = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

export const ReportPointComponent = () => {
  const navigate = useNavigate();

  const printReportRef = useRef(null);
  const chartRef = useRef(null);

  const { showToast } = useAppContext();

  const loyaltyReportData = JSON.parse(localStorage.getItem('loyalty-data-storage'));

  useEffect(() => {
    if (!loyaltyReportData) {
      showToast({ type: 'error', message: 'Halaman sebelumnya tidak tersedia.' });
      return navigate('/dashboard-point');
    }

    if (!printReportRef.current) return;
    if (!loyaltyReportData.download_allowed) return;
    if (!chartRef.current) return;

    const printHandle = async () => {
      const dataUrl = await toPng(printReportRef.current, { pixelRatio: 4 });

      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;

        const pdfWidth = 215.9;
        const pdfHeight = 355.6;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
        });

        const pxToMm = (px) => px * 0.264583;

        const mmWidth = pxToMm(imgWidth);
        const mmHeight = pxToMm(imgHeight);

        const scale = Math.min(pdfWidth / mmWidth, pdfHeight / mmHeight);

        const finalWidth = mmWidth * scale;
        const finalHeight = mmHeight * scale;

        const marginX = (pdfWidth - finalWidth) / 2;
        const marginY = (pdfHeight - finalHeight) / 2;

        pdf.addImage(dataUrl, 'PNG', marginX, marginY, finalWidth, finalHeight);
        pdf.save('loyalty-point-report.pdf');

        const newLoyaltyReportData = {
          ...loyaltyReportData,
          download_allowed: false,
        };

        localStorage.setItem('loyalty-data-storage', JSON.stringify(newLoyaltyReportData));
      };
    };

    const timeout = setTimeout(() => {
      printHandle();
    }, 2000);

    return () => clearTimeout(timeout);
  }, [chartRef.current, printReportRef.current]);

  return (
    <div className="report-point-wrapper">
      <div className="report-point-content" ref={printReportRef}>
        <header className="report-point-content__header">
          <div className="report-point-content__header__title">
            <h1>Report Point</h1>
            <p>
              Periode {`${loyaltyReportData?.report_period?.start ? loyaltyReportData.report_period.start : ''} Jan 01`}{' '}
              -{' '}
              {loyaltyReportData?.report_period?.end
                ? format(new Date(loyaltyReportData.report_period.end), 'yyyy MMM dd', { locale: id })
                : ''}
            </p>
          </div>
          <GetAndRideLogo className="report-point-content__header__logo" />
        </header>
        <main className="report-point-content__body">
          <div className="report-point-content__body__wrapper">
            <div className="points">
              <div className="points__total-point">
                <div
                  className="points__total-point__title"
                  style={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}
                >
                  <span>Total Point</span>
                  <span />
                </div>
                <p className="points__total-point__point">
                  {loyaltyReportData?.loyalty_report_data?.total_points_active || 0}
                </p>
              </div>
              <div className="points__point-redeemed">
                <div className="points__point-redeemed__title">
                  <span>Point Terpakai</span>
                  <span />
                </div>
                <p className="points__point-redeemed__point">
                  {loyaltyReportData?.loyalty_report_data?.total_points_redeemed || 0}
                </p>
              </div>
              <div className="points__point-earned">
                <div className="points__point-earned__title">
                  <span>Point Belum Dipakai</span>
                  <span />
                </div>
                <p className="points__point-earned__point">
                  {loyaltyReportData?.loyalty_report_data?.total_points_earned || 0}
                </p>
              </div>
            </div>
          </div>

          {loyaltyReportData?.chart_data && loyaltyReportData?.chart_options && (
            <div className="report-point-content__body__chart">
              <h2 className="report-point-content__body__chart__title">Report Details</h2>

              <div className="report-point-content__body__chart__line-chart">
                <Line data={loyaltyReportData?.chart_data} options={loyaltyReportData.chart_options} ref={chartRef} />
              </div>
            </div>
          )}

          {loyaltyReportData?.loyalty_report_data?.monthly_trends && (
            <div className="report-point-content__body__table-point">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Total Point</th>
                    <th>Point Terpakai</th>
                    <th>Point Belum Dipakai</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((item, index) => (
                    <tr key={index}>
                      <td>{item}</td>
                      <td>
                        {loyaltyReportData.loyalty_report_data.monthly_trends.find(
                          (dat) => format(new Date(dat.month), 'MMMM', { locale: id }) === item,
                        )?.points_active || 0}
                      </td>
                      <td>
                        {loyaltyReportData.loyalty_report_data.monthly_trends.find(
                          (dat) => format(new Date(dat.month), 'MMMM', { locale: id }) === item,
                        )?.points_redeemed || 0}
                      </td>
                      <td>
                        {loyaltyReportData.loyalty_report_data.monthly_trends.find(
                          (dat) => format(new Date(dat.month), 'MMMM', { locale: id }) === item,
                        )?.points_earned || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
